import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OfficeReviewsService } from '../services';
import { OFFICE_REVIEWS_QUERY_KEY } from './useGetOfficeReviews';

/**
 * Deletes an office review and refetches that office's list.
 *
 * Invalidating rather than filtering a local copy is the point of doing this
 * here: the row count the infinite scroll pages against comes from the server's
 * `total`, so a client-side removal would leave the two disagreeing.
 *
 * Deliberately knows nothing about Redux. Announcing the result is the
 * consumer's job — pass `onSuccess`/`onError` to `deleteReview`, or use the
 * returned flags. A `SHOW_MESSAGE` dispatch in here would put a UI concern
 * inside the domain layer and re-couple it to the store this feature is moving
 * off.
 *
 * @param {Object} params
 * @param {string} params.officeId - Scopes the query key to invalidate
 */
export const useDeleteOfficeReview = ({ officeId }) => {
  const queryClient = useQueryClient();

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: (reviewId) => OfficeReviewsService.deleteOfficeReview(reviewId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [OFFICE_REVIEWS_QUERY_KEY, officeId] }),
    // As in `useGetOfficeReviews`: the caller reports the outcome, so suppress
    // `queryClient`'s MutationCache.onError toast rather than showing both it
    // and the caller's message.
    meta: { skipGlobalErrorToaster: true },
  });

  return { deleteReview: mutate, isDeleting: isPending, isError, error };
};
